import Upload from 'antd/es/upload';
import message from 'antd/es/message';
import { t } from 'i18next';
import { ComponentProps } from 'react';
import UploadOutlined from '@ant-design/icons/UploadOutlined';

import { OpButton } from '../OpButton/OpButton';

interface OpUploadProps
  extends Omit<ComponentProps<typeof Upload>, 'value' | 'onChange'> {
  testId?: string;
  buttonText?: string;
  maxFileSize?: number; // in bytes, defaults to 1MB
  value?: ComponentProps<typeof Upload>['fileList'];
  onChange?: (value: ComponentProps<typeof Upload>['fileList']) => void;
  gtm?: string;
}

export const SosUpload = ({
  testId = 'op-upload',
  gtm,
  buttonText = t('Select file'),
  maxFileSize = 10 * 1024 * 1024, // 10MB in bytes
  value,
  onChange,
  ...uploadProps
}: OpUploadProps) => {
  return (
    <Upload
      beforeUpload={(file) => {
        // Check file size
        if (maxFileSize && file.size > maxFileSize) {
          message.error({
            content: t('File size must be less than {{maxSize}}MB', {
              maxSize: maxFileSize / 1024 / 1024,
            }),
            duration: 3,
          });

          return Upload.LIST_IGNORE;
        }

        // Check if file is an image type for thumbnail support
        const isImageFile = file.type.startsWith('image/');

        // Create appropriate URLs based on file type
        let thumbUrl: string;
        let url: string;

        if (isImageFile) {
          // For images, create object URL for thumbnail
          thumbUrl = URL.createObjectURL(file);
          url = URL.createObjectURL(file);
        } else {
          // For other file types, use a generic document icon
          thumbUrl = '';
          url = URL.createObjectURL(file);
        }

        onChange?.([
          ...(value || []),
          {
            uid: file.uid,
            name: file.name,
            type: file.type,
            status: 'done',
            originFileObj: file,
            thumbUrl,
            url, // This enables clicking to open the file
          },
        ]);

        return Upload.LIST_IGNORE;
      }}
      onRemove={(file) => {
        onChange?.(value?.filter((f) => f.uid !== file.uid));
      }}
      listType="picture"
      fileList={value || []}
      data-testid={testId}
      data-gtm={gtm}
      {...uploadProps}
    >
      <OpButton icon={<UploadOutlined />}>{buttonText}</OpButton>
    </Upload>
  );
};
